import React, { useEffect, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  MAPBOX_STYLE_SPRITE_ICONS,
  mapboxSpriteJsonUrl,
  mapboxSpritePngUrl,
  mapboxStylePathFromUrl,
  resolveSavedSpriteIcon,
} from './webMapMapboxUtils';

interface SpriteEntry {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio?: number;
}

const LABELS: Record<string, string> = {
  'dot-11': 'Dot',
  'communications-tower': 'Tower',
};

function labelFor(id: string): string {
  return LABELS[id] ?? id.replace(/-/g, ' ');
}

interface MapboxSpriteIconPickerProps {
  accessToken: string;
  mapStyleUrl: string;
  selectedIconId: string | undefined;
  onSelectIcon: (iconId: string) => void;
  onClearIcon: () => void;
}

const MapboxSpriteIconPicker: React.FC<MapboxSpriteIconPickerProps> = ({
  accessToken,
  mapStyleUrl,
  selectedIconId,
  onSelectIcon,
  onClearIcon,
}) => {
  const stylePath = mapboxStylePathFromUrl(mapStyleUrl);
  const [spriteJson, setSpriteJson] = useState<Record<string, SpriteEntry> | null>(null);
  const [spritePngUrl, setSpritePngUrl] = useState<string | null>(null);
  const [sheetW, setSheetW] = useState(0);
  const [sheetH, setSheetH] = useState(0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setSpriteJson(null);
      setSpritePngUrl(null);
      setLoadError(true);
      return;
    }
    let cancelled = false;
    setLoadError(false);
    const png = mapboxSpritePngUrl(stylePath, accessToken);
    setSpritePngUrl(png);

    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setSheetW(img.naturalWidth);
        setSheetH(img.naturalHeight);
      }
    };
    img.onerror = () => {
      if (!cancelled) setLoadError(true);
    };
    img.src = png;

    fetch(mapboxSpriteJsonUrl(stylePath, accessToken))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('sprite json'))))
      .then((j: Record<string, SpriteEntry>) => {
        if (!cancelled) setSpriteJson(j);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, stylePath]);

  const resolvedSelected = selectedIconId ? resolveSavedSpriteIcon(selectedIconId) : undefined;
  const canPaint = spriteJson && spritePngUrl && sheetW > 0 && sheetH > 0 && !loadError;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Marker previews use the current basemap’s sprite (change basemap above to refresh). Tooltip shows the icon name.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ maxHeight: 200, overflow: 'auto' }}>
        <Tooltip title="Default circle (no icon)">
          <Box
            component="button"
            type="button"
            onClick={onClearIcon}
            sx={{
              width: 40,
              height: 40,
              border: 2,
              borderColor: !selectedIconId ? 'primary.main' : 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: !selectedIconId ? 'action.selected' : 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </Box>
        </Tooltip>

        {MAPBOX_STYLE_SPRITE_ICONS.map((iconId) => {
          const def = spriteJson?.[iconId];
          const selected = resolvedSelected === iconId;
          const showSprite = canPaint && def;

          return (
            <Tooltip key={iconId} title={labelFor(iconId)}>
              <Box
                component="button"
                type="button"
                onClick={() => onSelectIcon(iconId)}
                sx={{
                  width: 40,
                  height: 40,
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: selected ? 'action.selected' : 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  overflow: 'hidden',
                }}
              >
                {showSprite ? (
                  <Box
                    sx={{
                      width: Math.min(28, def.width + 4),
                      height: Math.min(28, def.height + 4),
                      backgroundImage: `url("${spritePngUrl}")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: `${sheetW}px ${sheetH}px`,
                      backgroundPosition: `-${def.x}px -${def.y}px`,
                    }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ fontSize: 9, lineHeight: 1, textAlign: 'center', px: 0.25 }}>
                    {iconId.slice(0, 3)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MapboxSpriteIconPicker;
